
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Edit, Trash2, Star, FileText } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { Tables } from '@/integrations/supabase/types';

type Node = Tables<'nodes'>;
type Discipline = Tables<'disciplines'>;

interface ContentTableSectionProps {
  nodes: Node[];
  disciplines: Discipline[];
  onFeatureToggle: (nodeId: string, currentFeatured: boolean) => void;
  onStatusChange: (nodeId: string, newStatus: string) => void;
  onDelete: (nodeId: string, title: string) => void;
}

const getStatusColor = (status: string) => {
  switch (status) {
    case 'published': return 'bg-white text-green-800';
    case 'in_review': return 'bg-white text-black';
    case 'draft': return 'bg-white text-black';
    case 'archived': return 'bg-red-100 text-red-800';
    default: return 'bg-white text-black';
  }
};

const getNodeTypeIcon = (nodeType: string) => {
  switch (nodeType) {
    case 'article': return '📄';
    case 'lesson': return '📚';
    case 'concept': return '💡';
    case 'reference': return '📖';
    default: return '📄';
  }
};

const ContentTableSection: React.FC<ContentTableSectionProps> = ({
  nodes,
  disciplines,
  onFeatureToggle,
  onStatusChange,
  onDelete,
}) => {
  if (!nodes || nodes.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            All Content (0 items)
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <FileText className="h-12 w-12 text-black mx-auto mb-4" />
            <p className="text-black">No content found</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          All Content ({nodes.length} items)
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Content</TableHead>
                <TableHead>Author</TableHead>
                <TableHead>Discipline</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Featured</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {nodes.map((node: any) => (
                <TableRow key={node.id}>
                  <TableCell>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span>{getNodeTypeIcon(node.node_type)}</span>
                        <div className="font-medium">{node.title}</div>
                      </div>
                      <Badge variant="outline" className="text-xs">
                        {node.node_type}
                      </Badge>
                    </div>
                  </TableCell>
                  
                  <TableCell>
                    <div className="text-sm">
                      {node.profiles?.full_name || node.profiles?.username || 'Unknown'}
                    </div>
                  </TableCell>
                  
                  <TableCell>
                    <Badge variant="outline" className="text-xs">
                      {node.disciplines?.name || 'No discipline'}
                    </Badge>
                  </TableCell>
                  
                  <TableCell>
                    <Select
                      value={node.status}
                      onValueChange={(value) => onStatusChange(node.id, value)}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="in_review">In Review</SelectItem>
                        <SelectItem value="published">Published</SelectItem>
                        <SelectItem value="archived">Archived</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  
                  <TableCell>
                    <Button
                      variant={node.featured ? "default" : "outline"}
                      size="sm"
                      onClick={() => onFeatureToggle(node.id, node.featured)}
                    >
                      <Star className={`h-4 w-4 ${node.featured ? 'fill-current' : ''}`} />
                    </Button>
                  </TableCell>
                  
                  <TableCell className="text-sm text-black">
                    {formatDistanceToNow(new Date(node.created_at), { addSuffix: true })}
                  </TableCell>
                  
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Button variant="outline" size="sm">
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => onDelete(node.id, node.title)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default ContentTableSection;
